import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { centsToInputString } from '@patrociconnect/shared';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Valores monetários trafegam em centavos inteiros; a formatação fica no shared
export { formatBRL, toCents, centsToInputString, MAX_CENTS } from '@patrociconnect/shared';

/**
 * Máscara de moeda para inputs: os dígitos digitados são lidos como centavos,
 * preenchendo da direita para a esquerda. Ex.: "123456" -> "1.234,56".
 * O resultado pode ser convertido com toCents().
 */
export function maskBRLInput(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '').replace(/^0+/, '').slice(0, 11);
  if (!digits) return '';
  return centsToInputString(Number(digits));
}

/**
 * Formata data no padrão brasileiro.
 * Regra: nunca usa vírgula entre data e hora. Usa "às" com acento.
 * Ex: "02/04/2026 às 14:30" (e não "02/04/2026, 14:30")
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const hasTime = options?.hour !== undefined || options?.minute !== undefined;

  if (hasTime) {
    // Formatar data e hora separadamente para controlar o formato
    const datePart = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);

    const timePart = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: options?.hour ?? '2-digit',
      minute: options?.minute ?? '2-digit',
      ...(options?.second !== undefined ? { second: options.second } : {}),
      hour12: false,
    }).format(d);

    return `${datePart} às ${timePart}`;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  }).format(d);
}

/**
 * Formata um valor inteiro com separador de milhar no padrão brasileiro.
 * Aceita number ou string contendo dígitos e ignora qualquer outro caractere.
 * Ex.: 1000 -> "1.000", "1000000" -> "1.000.000".
 */
export function formatIntegerBR(value: number | string): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('pt-BR');
}

/**
 * Converte uma string formatada (com pontos/vírgulas/etc.) em número inteiro.
 * Retorna 0 se não houver dígitos.
 */
export function parseIntegerBR(value: string): number {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits ? Number(digits) : 0;
}

export function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
  }
  return value.toLocaleString('pt-BR');
}

export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'Email ou senha incorretos. Verifique seus dados e tente novamente.',
  'auth/user-not-found': 'Nenhuma conta encontrada com esse email.',
  'auth/wrong-password': 'Senha incorreta. Tente novamente.',
  'auth/email-already-in-use': 'Esse email já está cadastrado. Tente fazer login.',
  'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
  'auth/invalid-email': 'O formato do email é inválido.',
  'auth/user-disabled': 'Essa conta foi desativada. Entre em contato com o suporte.',
  'auth/too-many-requests': 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.',
  'auth/network-request-failed': 'Falha na conexão. Verifique sua internet e tente novamente.',
  'auth/requires-recent-login': 'Por segurança, faça login novamente para continuar.',
  'auth/invalid-verification-code': 'Código de verificação inválido. Confira o app autenticador e tente de novo.',
  'auth/missing-code': 'Informe o código de 6 dígitos do app autenticador.',
  'auth/totp-challenge-timeout': 'O tempo para informar o código expirou. Entre novamente.',
  'auth/multi-factor-info-not-found': 'Este segundo fator não está mais cadastrado na conta.',
  'auth/maximum-second-factor-count-exceeded': 'Limite de fatores de verificação atingido. Remova um antes de adicionar outro.',
  'auth/unsupported-first-factor': 'Este método de login não suporta verificação em duas etapas.',
  'auth/unverified-email': 'Confirme seu e-mail antes de ativar a verificação em duas etapas.',
  'auth/account-exists-with-different-credential': 'Já existe uma conta com esse email usando outro método de login.',
  'auth/expired-action-code': 'Esse link expirou. Solicite um novo.',
  'auth/invalid-action-code': 'Esse link é inválido ou já foi utilizado.',
  'auth/operation-not-allowed': 'Operação não permitida. Entre em contato com o suporte.',
  'auth/popup-closed-by-user': 'Login cancelado. A janela foi fechada antes de concluir.',
};

export function getFirebaseErrorMessage(error: unknown, fallback = 'Ocorreu um erro inesperado. Tente novamente.'): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    if (FIREBASE_ERROR_MESSAGES[code]) {
      return FIREBASE_ERROR_MESSAGES[code];
    }
  }
  return fallback;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Retorna uma URL segura para uso como href. Bloqueia javascript:, data:, vbscript:.
 * Se a URL for inválida, retorna '#'.
 */
export function safeHref(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return '#';
  const trimmed = url.trim();
  if (!trimmed) return '#';
  try {
    const parsed = new URL(trimmed);
    if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
      return trimmed;
    }
  } catch {
    // URLs relativas são ok para links internos, mas links sociais devem ser absolutos
  }
  return '#';
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return 'agora mesmo';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min atrás`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h atrás`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d atrás`;

  return formatDate(d);
}
